// apps/web/src/services/analisisService.ts
import { ApiClient } from './apiClient';

// ===============================
// INTERFACES
// ===============================

export interface FiltrosDisponibles {
  maquinas: Array<{
    id: number;
    codigo: string;
    descripcion: string;
    tipo: string;
  }>;
  operadores: Array<{
    id: number;
    codigo: string;
    descripcion: string;
    nombre_completo?: string;
  }>;
  faenas: Array<{
    id: number;
    codigo: string;
    descripcion: string;
    tipo?: string;
  }>;
  predios: Array<{
    id: number;
    codigo: string;
    descripcion: string;
    propietario?: string;
    superficie_hectareas?: number;
  }>;
  rodales: Array<{
    id: number;
    codigo: string;
    descripcion: string;
    superficie_hectareas?: number;
  }>;
  rango_fechas: {
    fecha_min: string | null;
    fecha_max: string | null;
  };
}

export interface AnalisisRequest {
  fecha_desde?: string;
  fecha_hasta?: string;
  maquinas_ids?: number[];
  operadores_ids?: number[];
  faenas_ids?: number[];
  predios_ids?: number[];
  rodales_ids?: number[];
  incluir_superposiciones?: boolean;
}

export interface MetricasResumen {
  total_reportes: number;
  total_horas: number;
  total_area_hectareas: number;
  area_promedio_por_reporte: number;
  horas_promedio_por_reporte: number;
}

export interface MetricaPorCategoria {
  codigo: string;
  descripcion: string;
  reportes: number;
  horas: number;
  area_hectareas: number;
}

export interface Metricas {
  resumen: MetricasResumen;
  por_maquina: MetricaPorCategoria[];
  por_operador: MetricaPorCategoria[];
  por_faena: MetricaPorCategoria[];
}

export interface ReporteSimplificado {
  id: number;
  numero: string;
  fecha: string;
  maquina?: {
    id: number;
    codigo: string;
    descripcion: string;
  };
  operador?: {
    id: number;
    codigo: string;
    descripcion: string;
  };
  estado: string;
  horometro_inicial: number;
  horometro_final: number;
  horas_trabajadas: number;
  total_faenas: number;
  faenas: Array<{
    faena_id: number;
    faena_codigo: string;
    faena_descripcion: string;
    horas: number;
    area_reporte: number;
  }>;
}

export interface GeometriaKML {
  id: number;
  nombre: string;
  descripcion?: string;
  area_hectareas: number;
  longitud_metros?: number; // ✅ NUEVO: para líneas
  tipo_geometria?: string; // ✅ NUEVO: 'POLYGON' o 'LINESTRING'
  medida_texto?: string; // ✅ NUEVO: texto descriptivo
  orden: number;
  geometria_wkt: string;
  archivo_kml: string;
  reporte: {
    id: number;
    numero: string;
    maquina_codigo: string;
    faena_codigo: string;
  };
  propiedades: {
    color: string;
    opacity: number;
    strokeWidth: number;
  };
}

export interface CentroMapa {
  latitud: number;
  longitud: number;
}

export interface SuperposicionDetalle {
  poligono_a_id: number;
  poligono_b_id: number;
  area_superpuesta_hectareas: number;
}

export interface AnalisisSuperposiciones {
  total_superposiciones: number;
  area_total_superpuesta_hectareas: number;
  detalle_superposiciones: SuperposicionDetalle[];
  porcentaje_area_superpuesta: number;
  error?: string;
}

export interface CriteriosAplicados {
  fecha_desde?: string;
  fecha_hasta?: string;
  maquinas_count: number;
  operadores_count: number;
  faenas_count: number;
  predios_count: number;
  rodales_count: number;
}

export interface ConsumoCombustible {
  total_cantidad: number;
  unidad: string;
  detalle: Array<{
    numero: string;
    fecha: string;
    fecha_str: string;
    maquina_descripcion: string;
    operador_descripcion: string;
    horometro_inicial: number;
    cantidad: number;
    unidad: string;
  }>;
}

export interface AnalisisResponse {
  success: boolean;
  criterios_aplicados: CriteriosAplicados;
  metricas: Metricas;
  consumo_combustible?: ConsumoCombustible;  // Opcional por si no hay datos
  reportes: ReporteSimplificado[];
  geometrias: GeometriaKML[];
  centro_mapa: CentroMapa;
  superposiciones?: AnalisisSuperposiciones;
  total_reportes: number;
  total_poligonos: number;
  total_lineas?: number; // ✅ NUEVO
  total_geometrias?: number; // ✅ NUEVO
}

export interface PDFBatchRequest {
  reportes_ids: number[];
  incluir_mapa?: boolean;
  zoom_mapa?: number;
  titulo_personalizado?: string;
  incluir_metricas?: boolean;
}

export interface PDFBatchResponse {
  success: boolean;
  message: string;
  reportes_incluidos: number;
  incluir_mapa: boolean;
  incluir_metricas: boolean;
  titulo: string;
}

// ===============================
// SERVICIO
// ===============================

export class AnalisisService {
  
  /**
   * Obtiene todos los filtros disponibles para análisis
   */
  static async obtenerFiltrosDisponibles(): Promise<FiltrosDisponibles> {
    try {
      // El backend retorna FiltrosDisponibles directamente
      return await ApiClient.get<FiltrosDisponibles>('/analisis/filtros-disponibles');
      
    } catch (error: any) {
      console.error('❌ Error al obtener filtros disponibles:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error obteniendo filtros disponibles';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Realiza análisis multi-criterio de reportes
   */
  static async analizarReportes(request: AnalisisRequest): Promise<AnalisisResponse> {
    try {
      // El backend retorna AnalisisResponse directamente
      return await ApiClient.post<any>('/analisis/analizar', request) as unknown as AnalisisResponse;
      
    } catch (error: any) {
      console.error('❌ Error al realizar análisis:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error realizando análisis de reportes';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Genera PDF batch desde análisis
   */
  static async generarPDFBatchFromAnalisis(request: PDFBatchRequest): Promise<PDFBatchResponse> {
    try {
      // El backend retorna PDFBatchResponse directamente  
      return await ApiClient.post<any>('/analisis/pdf-batch-from-analisis', request) as unknown as PDFBatchResponse;
      
    } catch (error: any) {
      console.error('❌ Error al generar PDF batch:', error);
      
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error generando PDF batch';
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Convierte geometrías WKT a coordenadas para Google Maps
   */
  static convertirWKTaCoordenadasMapa(geometriaWKT: string): google.maps.LatLngLiteral[] {
    try {
      if (!geometriaWKT || typeof geometriaWKT !== 'string') {
        console.error('❌ WKT inválido:', geometriaWKT);
        return [];
      }
      // Parsear POLYGON((lng lat, lng lat, ...))
      const coordsMatch = geometriaWKT.match(/POLYGON\s*\(\s*\((.*?)\)\s*\)/i);
      if (!coordsMatch) {
        console.error('❌ Formato WKT no válido - no match:', geometriaWKT);
        return [];
      }
      
      const coordsStr = coordsMatch[1];
      const coordPairs = coordsStr.split(',').map(pair => pair.trim()).filter(pair => pair.length > 0);
      const coordinates = coordPairs.map(pair => {
        const [lng, lat] = pair.split(/\s+/).map(Number);
        
        // Validar que las coordenadas sean válidas
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          console.warn('⚠️ Coordenada inválida:', { lat, lng, pair });
          return null;
        }
        
        return { lat, lng };
      }).filter(coord => coord !== null) as google.maps.LatLngLiteral[];      
      return coordinates;
    } catch (error) {
      console.error('❌ Error convirtiendo WKT a coordenadas:', error);
      return [];
    }
  }

  /**
   * Calcula el centro de múltiples geometrías
   */
  static calcularCentroGeometrias(geometrias: GeometriaKML[]): CentroMapa {
    if (geometrias.length === 0) {
      return { latitud: -33.4489, longitud: -70.6693 }; // Chile central default
    }

    let totalLat = 0;
    let totalLng = 0;
    let totalPuntos = 0;

    geometrias.forEach(geom => {
      const coords = this.convertirWKTaCoordenadasMapa(geom.geometria_wkt);
      coords.forEach(coord => {
        totalLat += coord.lat;
        totalLng += coord.lng;
        totalPuntos++;
      });
    });

    return {
      latitud: totalLat / totalPuntos,
      longitud: totalLng / totalPuntos
    };
  }

  /**
   * Agrupa reportes por criterio específico
   */
  static agruparReportesPor(
    reportes: ReporteSimplificado[], 
    criterio: 'maquina' | 'operador' | 'fecha'
  ): Record<string, ReporteSimplificado[]> {
    return reportes.reduce((grupos, reporte) => {
      let clave: string;
      
      switch (criterio) {
        case 'maquina':
          clave = reporte.maquina?.codigo || 'Sin Máquina';
          break;
        case 'operador':
          clave = reporte.operador?.codigo || 'Sin Operador';
          break;
        case 'fecha':
          clave = reporte.fecha;
          break;
        default:
          clave = 'Otros';
      }
      
      if (!grupos[clave]) {
        grupos[clave] = [];
      }
      grupos[clave].push(reporte);
      
      return grupos;
    }, {} as Record<string, ReporteSimplificado[]>);
  }

  /**
   * Exporta datos de análisis a CSV (ACTUALIZADO con líneas)
   */
  static exportarAnalisisCSV(analisis: AnalisisResponse): void {
    const { reportes, geometrias, metricas, total_poligonos, total_lineas } = analisis;
    
    // ✅ CREAR RESUMEN en la primera hoja
    const longitudTotalKm = geometrias
      .filter(g => g.tipo_geometria === 'LINESTRING')
      .reduce((total, linea) => total + (linea.longitud_metros || 0), 0) / 1000;
    
    const csvContent = [
      // Encabezado del resumen
      '=== RESUMEN DEL ANÁLISIS ===',
      `Total Reportes,${analisis.total_reportes}`,
      `Total Polígonos,${total_poligonos || 0}`,
      `Total Líneas,${total_lineas || 0}`,
      `Total Horas,${metricas.resumen.total_horas.toFixed(2)}`,
      `Total Área (ha),${metricas.resumen.total_area_hectareas.toFixed(2)}`,
      `Longitud Total Líneas (km),${longitudTotalKm.toFixed(2)}`,
      '',
      
      // Tabla de reportes
      '=== REPORTES ===',
      'ID,Número,Fecha,Máquina,Operador,Estado,Horas,Total Faenas,Área Total (ha)',
      ...reportes.map(reporte => [
        reporte.id,
        reporte.numero,
        reporte.fecha,
        reporte.maquina?.codigo || '',
        reporte.operador?.codigo || '',
        reporte.estado,
        reporte.horas_trabajadas,
        reporte.total_faenas,
        reporte.faenas.reduce((sum, f) => sum + f.area_reporte, 0).toFixed(2)
      ].join(',')),
      '',
      
      // ✅ NUEVA SECCIÓN: Geometrías (polígonos + líneas)
      '=== GEOMETRÍAS KML ===',
      'ID Geometría,Nombre,Tipo,Reporte,Máquina,Área (ha),Longitud (km),Archivo KML',
      ...geometrias.map(geom => [
        geom.id,
        geom.nombre,
        geom.tipo_geometria || (geom.area_hectareas > 0 ? 'POLYGON' : 'LINESTRING'),
        geom.reporte.numero,
        geom.reporte.maquina_codigo,
        geom.area_hectareas.toFixed(2),
        ((geom.longitud_metros || 0) / 1000).toFixed(2),
        geom.archivo_kml
      ].join(',')),
      '',
      
      // Métricas por máquina
      '=== MÉTRICAS POR MÁQUINA ===',
      'Código,Descripción,Reportes,Horas,Área (ha)',
      ...metricas.por_maquina.map(m => [
        m.codigo,
        m.descripcion,
        m.reportes,
        m.horas.toFixed(2),
        m.area_hectareas.toFixed(2)
      ].join(',')),
      '',
      
      // Métricas por operador
      '=== MÉTRICAS POR OPERADOR ===',
      'Código,Descripción,Reportes,Horas,Área (ha)',
      ...metricas.por_operador.map(o => [
        o.codigo,
        o.descripcion,
        o.reportes,
        o.horas.toFixed(2),
        o.area_hectareas.toFixed(2)
      ].join(','))
    ].join('\n');
    
    // Descargar archivo
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analisis_completo_${new Date().getTime()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }
}

export default AnalisisService;
